// Nevada lien documents (NRS chapter 108), word for word from the statute:
// the four waiver-and-release forms of NRS 108.2457(5) and the Notice of
// Right to Lien of NRS 108.245. A waiver "is unenforceable unless it is in
// the following forms", so the wording here must not be edited; only the
// blanks are filled. The company (the lien claimant) signs them, not the
// customer. Lines starting "!! " are the statutory notice that must print
// "in type at least as large as the largest type otherwise on the document".
// Text checked against https://www.leg.state.nv.us/NRS/NRS-108.html (2026-10-24).

export const LIEN_WAIVER_KINDS = ["conditional_progress", "unconditional_progress", "conditional_final", "unconditional_final"] as const;
export type LienWaiverKind = (typeof LIEN_WAIVER_KINDS)[number];
export type LienDocKind = LienWaiverKind | "notice";

export const LIEN_DOC_TITLE: Record<LienDocKind, string> = {
  conditional_progress: "Conditional Waiver and Release Upon Progress Payment",
  unconditional_progress: "Unconditional Waiver and Release Upon Progress Payment",
  conditional_final: "Conditional Waiver and Release Upon Final Payment",
  unconditional_final: "Unconditional Waiver and Release Upon Final Payment",
  notice: "Notice of Right to Lien",
};

/** When each one is used, in plain words (NRS 108.2457(5)(a)-(d)). */
export const LIEN_WAIVER_WHEN: Record<LienWaiverKind, string> = {
  conditional_progress: "Asked for a waiver to get a progress payment you haven't received yet (or are paid by check). Takes effect only when the check clears.",
  unconditional_progress: "You've been paid (all or part of) a progress billing. Waives only the amount actually received.",
  conditional_final: "Asked for a waiver to get the final payment you haven't received yet (or are paid by check). Takes effect only when the check clears.",
  unconditional_final: "You've been paid the final billing in full. Waives everything except disputed claims you list.",
};

/** A sub must send the Notice of Right to Lien within this many days of first work to keep lien rights for all of it (NRS 108.245(6)). */
export const LIEN_NOTICE_DAYS = 31;

export interface LienWaiverFields {
  propertyName: string;
  propertyLocation: string;
  customer: string;
  invoiceNumber: string;
  paymentAmount: string;
  /** Conditional final only. */
  paymentPeriod?: string;
  /** Final waivers only. */
  disputedClaims?: string;
  dated: string;
  companyName: string;
}

const RELEASE_WORDS = "any notice of lien, any private bond right, any claim for payment and any rights under any similar ordinance, rule or statute related to payment rights that the undersigned has on the above-described Property";

export function lienWaiverBody(kind: LienWaiverKind, f: LienWaiverFields): string {
  const head = [
    `Property Name: ${f.propertyName}`,
    `Property Location: ${f.propertyLocation}`,
    `Undersigned's Customer: ${f.customer}`,
    `Invoice/Payment Application Number: ${f.invoiceNumber}`,
    `Payment Amount: ${f.paymentAmount}`,
    ...(kind === "conditional_final" ? [`Payment Period: ${f.paymentPeriod ?? ""}`] : []),
    ...(kind === "conditional_final" || kind === "unconditional_final" ? [`Amount of Disputed Claims: ${f.disputedClaims ?? ""}`] : []),
  ].join("\n");
  const sign = `Dated: ${f.dated}\n${f.companyName}\n(Company Name)`;
  const verify = "Before any recipient of this document relies on it, the recipient should verify evidence of payment to the undersigned. ";
  const progressScope = (and: "or" | "and") =>
    `This release covers a progress payment for the work, materials ${and} equipment furnished by the undersigned to the Property or to the Undersigned's Customer which are the subject of the Invoice or Payment Application, but only to the extent of the Payment Amount or such portion of the Payment Amount as the undersigned is actually paid, and does not cover any retention withheld, any items, modifications or changes pending approval, disputed items and claims, or items furnished that are not paid. `;
  const warrantProgress = "The undersigned warrants that he or she either has already paid or will use the money received from this progress payment promptly to pay in full all laborers, subcontractors, materialmen and suppliers for all work, materials or equipment that are the subject of this waiver and release.";
  const uponCheck = `Upon receipt by the undersigned of a check in the above-referenced Payment Amount payable to the undersigned, and when the check has been properly endorsed and has been paid by the bank on which it is drawn, this document becomes effective to release and the undersigned shall be deemed to waive ${RELEASE_WORDS} to the following extent:`;
  switch (kind) {
    case "conditional_progress":
      return [head, uponCheck, progressScope("or") + verify + warrantProgress, sign].join("\n\n");
    case "unconditional_progress":
      return [
        head,
        `The undersigned has been paid and has received a progress payment in the above-referenced Payment Amount for all work, materials and equipment the undersigned furnished to the Customer for the above-described Property and does hereby waive and release ${RELEASE_WORDS} to the following extent:`,
        progressScope("and") + warrantProgress,
        sign,
        "!! Notice: This document waives rights unconditionally and states that you have been paid for giving up those rights. This document is enforceable against you if you sign it to the extent of the Payment Amount or the amount received. If you have not been paid, use a conditional release form.",
      ].join("\n\n");
    case "conditional_final":
      return [
        head,
        uponCheck,
        "This release covers the final payment to the undersigned for all work, materials or equipment furnished by the undersigned to the Property or to the Undersigned's Customer and does not cover payment for Disputed Claims, if any. " +
          verify +
          "The undersigned warrants that he or she either has already paid or will use the money received from the final payment promptly to pay in full all laborers, subcontractors, materialmen and suppliers for all work, materials or equipment that are the subject of this waiver and release.",
        sign,
      ].join("\n\n");
    case "unconditional_final":
      return [
        head,
        `The undersigned has been paid in full for all work, materials and equipment furnished to the Customer for the above-described Property and does hereby waive and release ${RELEASE_WORDS}, except for the payment of Disputed Claims, if any, noted above. The undersigned warrants that he or she either has already paid or will use the money received from this final payment promptly to pay in full all laborers, subcontractors, materialmen and suppliers for all work, materials and equipment that are the subject of this waiver and release.`,
        sign,
        "!! Notice: This document waives rights unconditionally and states that you have been paid for giving up those rights. This document is enforceable against you if you sign it, even if you have not been paid. If you have not been paid, use a conditional release form.",
      ].join("\n\n");
  }
}

export interface LienNoticeFields {
  ownerNameAddress: string;
  workDescription: string;
  property: string;
  contractWith: string;
  companyName: string;
}

/** NRS 108.245(1), with the blanks filled. */
export function lienNoticeBody(f: LienNoticeFields): string {
  return [
    `To: ${f.ownerNameAddress}`,
    "(Owner's name and address)",
    "The undersigned notifies you that he or she has supplied materials or equipment or performed work or services as follows:",
    f.workDescription,
    "(General description of materials, equipment, work or services)",
    `for improvement of property identified as ${f.property} under contract with ${f.contractWith}. This is not a notice that the undersigned has not been or does not expect to be paid, but a notice required by law that the undersigned may, at a future date, record a notice of lien as provided by law against the property if the undersigned is not paid.`,
    `${f.companyName}\n(Claimant)`,
  ].join("\n\n");
}

export interface LienNoticeItem {
  /** "lot:<id>", "nolot" (jobs on the project with no lot), or "project" (a project without lots). */
  key: string;
  lotId: string | null;
  label: string;
  /** First day work happened there (a job started, in progress or done). */
  firstWorkAt: number | null;
  /** firstWorkAt + 31 days. */
  dueAt: number | null;
  noticeId: string | null;
  sentAt: number | null;
  status: "not_started" | "due" | "due_soon" | "overdue" | "prepared" | "sent";
}

const DAY = 86_400_000;

/**
 * Where a project stands on Notices of Right to Lien: one line per lot (or one
 * for the project when it has no lots). Shared by the project page and the
 * daily reminder so both say the same thing.
 */
export function lienNoticeItems(input: {
  lots: Array<{ id: string; name: string }>;
  jobs: Array<{ lotId?: string | null; startAt: number | null; doneAt?: number | null; status: string }>;
  notices: Array<{ id: string; lotId?: string | null; sentAt?: number | null; createdAt: number; status: string }>;
  now: number;
}): LienNoticeItem[] {
  const workAt = (j: (typeof input.jobs)[number]): number | null => {
    if (j.status === "canceled") return null;
    if (j.status === "in_progress" || j.status === "done") return j.startAt ?? j.doneAt ?? null;
    return j.startAt !== null && j.startAt <= input.now ? j.startAt : null;
  };
  const units: Array<{ key: string; lotId: string | null; label: string }> = input.lots.length
    ? input.lots.map((l) => ({ key: `lot:${l.id}`, lotId: l.id, label: l.name }))
    : [{ key: "project", lotId: null, label: "The whole project" }];
  const lotIds = new Set(input.lots.map((l) => l.id));
  if (input.lots.length && input.jobs.some((j) => !j.lotId || !lotIds.has(j.lotId))) units.push({ key: "nolot", lotId: null, label: "Jobs with no lot" });
  return units.map((u) => {
    const jobs = input.jobs.filter((j) => (u.key === "project" ? true : u.key === "nolot" ? !j.lotId || !lotIds.has(j.lotId) : j.lotId === u.lotId));
    const times = jobs.map(workAt).filter((t): t is number => t !== null);
    const firstWorkAt = times.length ? Math.min(...times) : null;
    const dueAt = firstWorkAt !== null ? firstWorkAt + LIEN_NOTICE_DAYS * DAY : null;
    const notice = input.notices
      .filter((n) => n.status !== "void" && (n.lotId ?? null) === u.lotId)
      .sort((a, b) => Number(!!b.sentAt) - Number(!!a.sentAt) || b.createdAt - a.createdAt)[0];
    const sentAt = notice?.sentAt ?? null;
    const status: LienNoticeItem["status"] = sentAt
      ? "sent"
      : firstWorkAt === null || dueAt === null
        ? notice
          ? "prepared"
          : "not_started"
        : input.now > dueAt
          ? "overdue"
          : notice
            ? "prepared"
            : dueAt - input.now <= 7 * DAY
              ? "due_soon"
              : "due";
    return { key: u.key, lotId: u.lotId, label: u.label, firstWorkAt, dueAt, noticeId: notice?.id ?? null, sentAt, status };
  });
}

/** How it must be delivered (NRS 108.245(1)): shown with the notice, not printed on it. */
export const LIEN_NOTICE_DELIVERY =
  "Deliver it to the property owner in person or by certified mail, and send a copy the same way to the prime contractor (for information only). Rights cover work from the 31 days before it's given onward.";
